import { useState } from 'react';
import { NotificationPermissionPrompt } from './NotificationPermissionPrompt';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Shield, Eye, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { firebaseMessagingService } from '../../config/firebase';
import { safeLog } from '@/utils/safeLog';

interface OnboardingFlowProps {
  userRole: 'student' | 'teacher' | 'admin';
  onComplete: (data: {
    notificationsGranted: boolean;
    permissions: Record<string, boolean>;
  }) => void;
}

type OnboardingStep = 'welcome' | 'notifications' | 'monitoring-consent' | 'ready';

export function OnboardingFlow({ userRole, onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [notificationsGranted, setNotificationsGranted] = useState(false);

  const handleNotificationsComplete = (granted: boolean) => {
    setNotificationsGranted(granted);

    if (granted) {
      const authUid = sessionStorage.getItem('authUid');
      if (authUid) {
        firebaseMessagingService.getTokenAndRegister(authUid).catch((error) => {
          safeLog.error('Failed to register FCM token:', error);
          // Optionally: track in analytics or show a toast notification
        });
      }
    }

    if (userRole === 'student') {
      setStep('monitoring-consent');
    } else {
      setStep('ready');
    }
  };

  const handleMonitoringConsent = () => {
    setStep('ready');
  };

  const handleFinish = () => {
    onComplete({
      notificationsGranted,
      permissions: { monitoringConsent: true }
    });
  };

  if (step === 'welcome') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 to-purple-700 p-4">
        <Card className="w-full max-w-lg text-center">
          <CardHeader className="pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center">
                <Shield className="w-10 h-10 text-indigo-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Welcome to ShieldED Haven!</CardTitle>
            <CardDescription className="text-base mt-2">
              {userRole === 'student'
                ? "Let's get you set up so you can stay safe online and earn rewards for being a great digital citizen."
                : userRole === 'teacher'
                ? "Let's set up your account so you can monitor and protect your students."
                : "Let's configure your admin account to manage your school's safety."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {userRole === 'student' && (
              <div className="text-left space-y-3 bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700">Here's what we'll set up:</p>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Enable notifications for safety alerts</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <Eye className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <span>Review how we keep you safe online</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                  <span>Start earning safety points and rewards</span>
                </div>
              </div>
            )}
            <Button onClick={() => setStep('notifications')} className="w-full" size="lg">
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'notifications') {
    return (
      <NotificationPermissionPrompt
        userRole={userRole}
        onComplete={handleNotificationsComplete}
      />
    );
  }

  if (step === 'monitoring-consent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-cyan-700 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <Eye className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Your Safety Matters</CardTitle>
            <CardDescription className="text-base mt-2">
              Here's how ShieldED Haven helps keep you safe online
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 bg-blue-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">AI-Powered Protection</p>
                  <p className="text-xs text-gray-600">Our AI monitors your digital activities to detect cyberbullying, threats, and harmful content — keeping you safe.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Self-Correction Opportunities</p>
                  <p className="text-xs text-gray-600">If something you type is flagged, you'll get a chance to correct it before anyone else is notified. You even earn points for self-correcting!</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Earn Rewards</p>
                  <p className="text-xs text-gray-600">Being a good digital citizen earns you safety points, levels, and achievements. Compete with classmates on the leaderboard!</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Privacy Note:</strong> Your school has enabled digital safety monitoring as part of their student protection policy. 
                Only flagged content is reviewed by teachers or administrators. Your regular browsing activity stays private.
              </p>
            </div>

            <Button onClick={handleMonitoringConsent} className="w-full" size="lg">
              I Understand, Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ready step
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 to-emerald-700 p-4">
      <Card className="w-full max-w-lg text-center">
        <CardHeader className="pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">You're All Set!</CardTitle>
          <CardDescription className="text-base mt-2">
            {userRole === 'student'
              ? "Your account is ready. Start exploring, earn safety points, and stay safe online!"
              : "Your account is configured. You can now access your dashboard."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleFinish} className="w-full" size="lg">
            Go to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}